// components/Header.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Box,
  Chip,
  Badge,
  Menu,
  MenuItem,
  Fade,
  Paper,
  Grid,
  Typography as MuiTypography,
  Collapse,
  Avatar,
  Tooltip,
  Zoom,
  Container,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CodeIcon from '@mui/icons-material/Code';
import PhoneIcon from '@mui/icons-material/Phone';
import CallIcon from '@mui/icons-material/Call';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PaletteIcon from '@mui/icons-material/Palette';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import ImageIcon from '@mui/icons-material/Image';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoIcon from '@mui/icons-material/Info';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ApiIcon from '@mui/icons-material/Api';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import EmailIcon from '@mui/icons-material/Email';
import School from '@mui/icons-material/School';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import logo from "../images/brand.png";
import { PriceCheck } from '@mui/icons-material';
// Custom animations
const blinkAnimation = keyframes`
  0%, 50% {
    opacity: 1;
    transform: scale(1);
  }
  51%, 100% {
    opacity: 0.3;
    transform: scale(0.95);
  }
`;

const slideInFromTop = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(139, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(139, 0, 0, 0.6), 0 0 30px rgba(139, 0, 0, 0.4);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

// Enhanced Products data with all your routes categorized
const productsData = [
  // Design Tools
  {
    id: 1,
    name: 'Logo Color Changer',
    description: 'Transform your brand identity instantly',
    path: '/logo-changer',
    icon: <PaletteIcon />,
    category: 'Design',
    featured: true,
    tags: ['Popular', 'Design'],
  },
  {
    id: 2,
    name: 'Color Palette Generator',
    description: 'Generate beautiful color schemes',
    path: '/color-palette',
    icon: <ColorLensIcon />,
    category: 'Design',
    tags: ['Design', 'Colors'],
  },

  // Marketing Tools
  {
    id: 3,
    name: 'QR Code Generator',
    description: 'Create stunning QR codes with analytics',
    path: '/qr-code-generator',
    icon: <QrCodeIcon />,
    category: 'Marketing',
    featured: true,
    tags: ['Featured', 'Marketing'],
  },
  {
    id: 4,
    name: 'Business QR',
    description: 'Professional QR codes for businesses',
    path: '/business-qr',
    icon: <QrCodeIcon />,
    category: 'Marketing',
    tags: ['Business', 'Marketing'],
  },

  // Development Tools
  {
    id: 5,
    name: 'Image Optimizer',
    description: 'Advanced compression without quality loss',
    path: '/image-optimizer',
    icon: <ImageIcon />,
    category: 'Development',
    tags: ['Performance', 'Tools'],
  },
  {
    id: 6,
    name: 'Code Formatter',
    description: 'Clean and format your code',
    path: '/code-formatter',
    icon: <IntegrationInstructionsIcon />,
    category: 'Development',
    tags: ['Developer', 'Tools'],
  },
  {
    id: 7,
    name: 'UI Components',
    description: 'Production-ready React components',
    path: '/ui-components',
    icon: <DesignServicesIcon />,
    category: 'Development',
    featured: true,
    tags: ['Development', 'React'],
  },
  {
    id: 8,
    name: 'API Hub',
    description: 'Test and manage your APIs',
    path: '/api-hub',
    icon: <ApiIcon />,
    category: 'Development',
    tags: ['API', 'Testing'],
  },
  {
    id: 9,
    name: 'Performance Analyzer',
    description: 'Analyze and optimize performance',
    path: '/performance-analyzer',
    icon: <SpeedIcon />,
    category: 'Development',
    tags: ['Performance', 'Tools'],
  },
  {
    id: 10,
    name: 'Developer Console',
    description: 'Advanced development tools',
    path: '/developer-console',
    icon: <DeveloperBoardIcon />,
    category: 'Development',
    tags: ['Developer', 'Tools'],
  },
  {
    id: 11,
    name: 'Media Scanner',
    description: 'Scan and analyze media files',
    path: '/media-scanner',
    icon: <ImageIcon />,
    category: 'Development',
    tags: ['Media', 'Tools'],
  },

  // Games & Entertainment
  {
    id: 12,
    name: 'Puzzle Hub',
    description: 'Collection of brain-teasing puzzles',
    path: '/puzzle-hub',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    featured: true,
    tags: ['Games', 'Puzzle'],
  },
  {
    id: 13,
    name: 'Snake Game',
    description: 'Classic snake game with modern twist',
    path: '/snake-game',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Classic'],
  },
  {
    id: 14,
    name: 'Sliding Puzzle',
    description: 'Challenge your spatial reasoning',
    path: '/sliding-puzzle',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Puzzle'],
  },
  {
    id: 15,
    name: 'Memory Match',
    description: 'Test your memory with matching cards',
    path: '/memory-match',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Memory'],
  },
  {
    id: 16,
    name: 'Color Match',
    description: 'Fast-paced color matching game',
    path: '/color-match',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Colors'],
  },
  {
    id: 17,
    name: 'Math Reflex',
    description: 'Improve your math skills quickly',
    path: '/math-reflex',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Education'],
  },
  {
    id: 18,
    name: 'Sequence Memory',
    description: 'Remember and repeat sequences',
    path: '/seq-memory',
    icon: <SportsEsportsIcon />,
    category: 'Games',
    tags: ['Games', 'Memory'],
  },

  // Services
  {
    id: 19,
    name: 'Email Service',
    description: 'Professional email solutions',
    path: '/email-service',
    icon: <EmailIcon />,
    category: 'Services',
    tags: ['Service', 'Business'],
  },
  {
    id: 20,
    name: 'Partnership Service',
    description: 'Collaborate and grow with us',
    path: '/partners-service',
    icon: <GroupsIcon />,
    category: 'Services',
    tags: ['Service', 'Business'],
  },

  // Education
  {
    id: 21,
    name: 'Student App',
    description: 'Comprehensive student management',
    path: '/student-app',
    target:'blank',
    icon: <School />,
    category: 'Education',
    featured: true,
    tags: ['Education', 'Students'],
  },
];

// Categories with counts
const categories = [
  {
    name: 'All Products',
    icon: <AutoAwesomeIcon />,
    count: productsData.length,
    description: 'Explore all our tools and services'
  },
  {
    name: 'Development',
    icon: <CodeIcon />,
    count: productsData.filter(p => p.category === 'Development').length,
    description: 'Developer tools and utilities'
  },
  {
    name: 'Design',
    icon: <PaletteIcon />,
    count: productsData.filter(p => p.category === 'Design').length,
    description: 'Design and creative tools'
  },
  {
    name: 'Marketing',
    icon: <TrendingUpIcon />,
    count: productsData.filter(p => p.category === 'Marketing').length,
    description: 'Marketing and business tools'
  },
  {
    name: 'Games',
    icon: <SportsEsportsIcon />,
    count: productsData.filter(p => p.category === 'Games').length,
    description: 'Fun and educational games'
  },
  {
    name: 'Services',
    icon: <GroupsIcon />,
    count: productsData.filter(p => p.category === 'Services').length,
    description: 'Professional services'
  },
  {
    name: 'Education',
    icon: <School />,
    count: productsData.filter(p => p.category === 'Education').length,
    description: 'Educational tools and apps'
  },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [productsAnchor, setProductsAnchor] = useState(null);
  const [companyAnchor, setCompanyAnchor] = useState(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  // Debounced scroll handler
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setProductsAnchor(null);
    setCompanyAnchor(null);
    setMobileOpen(false);
  }, [location]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleCallClick = useCallback(() => {
    window.open('tel:+916382585971', '_self');
  }, []);

  const handleProductsMenuOpen = useCallback((event) => {
    setProductsAnchor(event.currentTarget);
  }, []);

  const handleProductsMenuClose = useCallback(() => {
    setProductsAnchor(null);
  }, []);

  const handleCompanyMenuOpen = useCallback((event) => {
    setCompanyAnchor(event.currentTarget);
  }, []);

  const handleCompanyMenuClose = useCallback(() => {
    setCompanyAnchor(null);
  }, []);

  const handleMobileProductsToggle = useCallback(() => {
    setMobileProductsOpen(prev => !prev);
  }, []);

  const handleMobileCompanyToggle = useCallback(() => {
    setMobileCompanyOpen(prev => !prev);
  }, []);
  const filteredProducts = useMemo(() =>
    productsData.filter(product =>
      selectedCategory === 'All Products' ||
      product.category === selectedCategory
    ), [selectedCategory]
  );

  // Company menu items
  const companyMenuItems = useMemo(() => [
    {
      text: 'About Us',
      path: '/about',
      icon: <InfoIcon sx={{ fontSize: 20 }} />,
      description: 'Learn about our journey',
      badge: 'New'
    },
    {
      text: 'Services',
      path: '/services',
      icon: <DesignServicesIcon sx={{ fontSize: 20 }} />,
      description: 'Our professional services'
    },
    {
      text: 'Partnership',
      path: '/partners-service',
      icon: <GroupsIcon sx={{ fontSize: 20 }} />,
      description: 'Collaborate and grow with us'
    },
    {
      text: 'Careers',
      path: '/careers',
      icon: <WorkIcon sx={{ fontSize: 20 }} />,
      description: 'Join our talented team',
      badge: 'Hiring'
    },
    {
      text: 'Training',
      path: '/training',
      icon: <SchoolIcon sx={{ fontSize: 20 }} />,
      description: 'Enhance your skills'
    },
    {
      text: 'Contact',
      path: '/contact',
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
      description: 'Get in touch with us'
    },
  ], []);

  const menuItems = useMemo(() => [
    { text: 'Home', path: '/' },
    {
      text: 'Products',
      path: '/products-tools',
      hasDropdown: true,
      badge: 'Apps& Tools'
    },
    {
      text: 'Company',
      path: '#',
      hasDropdown: true,
      isCompany: true
    },
    {
      text: 'Pricing',
      icon: <PriceCheck/>,
      path: '/pricing'
    },
  ], []);

  // Enhanced Products Dropdown Component
  const ProductsDropdown = useCallback(() => (
    <Paper
      sx={{
        width: isMobile ? '95vw' : 1000,
        maxWidth: '95vw',
        background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
        border: '1px solid rgba(139, 0, 0, 0.3)',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(20px)',
        animation: `${slideInFromTop} 0.3s ease-out`,
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <Grid container>
        {/* Categories Sidebar */}
        <Grid item xs={12} md={3} sx={{
          borderRight: { md: '1px solid rgba(139, 0, 0, 0.2)' },
          background: 'rgba(10, 17, 40, 0.8)',
        }}>
          <Box sx={{ p: 2 }}>
            <MuiTypography
              variant="h6"
              sx={{
                color: 'white',
                mb: 2,
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #ff4d4d, #8b0000)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              Categories
            </MuiTypography>
            {categories.map((category) => (
              <Tooltip key={category.name} title={category.description} placement="right" arrow>
                <Box
                  onClick={() => setSelectedCategory(category.name)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    mb: 1,
                    background: selectedCategory === category.name
                      ? 'linear-gradient(90deg, rgba(139, 0, 0, 0.3) 0%, transparent 100%)'
                      : 'transparent',
                    border: selectedCategory === category.name
                      ? '1px solid rgba(255, 77, 77, 0.5)'
                      : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(139, 0, 0, 0.2) 0%, transparent 100%)',
                      border: '1px solid rgba(255, 77, 77, 0.3)',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <Box sx={{
                    color: selectedCategory === category.name ? '#ff4d4d' : 'rgba(255,255,255,0.7)',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {category.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MuiTypography
                      variant="body2"
                      sx={{
                        color: selectedCategory === category.name ? '#ff4d4d' : 'white',
                        fontWeight: selectedCategory === category.name ? '600' : '400',
                        fontSize: '0.9rem',
                      }}
                    >
                      {category.name}
                    </MuiTypography>
                    <MuiTypography
                      variant="caption"
                      sx={{
                        color: selectedCategory === category.name ? '#ff9999' : 'rgba(255,255,255,0.5)',
                        display: 'block',
                      }}
                    >
                      {category.description}
                    </MuiTypography>
                  </Box>
                  <Chip
                    label={category.count}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: 'rgba(255, 77, 77, 0.2)',
                      color: '#ff4d4d',
                      fontWeight: 'bold',
                      minWidth: 24,
                    }}
                  />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Grid>

        {/* Products Grid */}
        <Grid item xs={12} md={9}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <MuiTypography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {selectedCategory}
                  <Chip
                    label={`${filteredProducts.length} tools`}
                    size="small"
                    color="primary"
                    sx={{
                      bgcolor: '#ff4d4d',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </MuiTypography>
                <MuiTypography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  {categories.find(cat => cat.name === selectedCategory)?.description}
                </MuiTypography>
              </Box>

              <Button
                component={Link}
                to="/products-tools"
                onClick={handleProductsMenuClose}
                variant="outlined"
                size="small"
                endIcon={<TrendingUpIcon />}
                sx={{
                  borderColor: '#ff4d4d',
                  color: '#ff4d4d',
                  '&:hover': {
                    borderColor: '#ff4d4d',
                    background: 'rgba(255, 77, 77, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View All
              </Button>
            </Box>

            <Grid container spacing={2}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} lg={4} key={product.id}>
                  <Tooltip title={`Explore ${product.name}`} arrow placement="top" TransitionComponent={Zoom}>
                    <Box
                      component={Link}
                      to={product.path}
                      onClick={handleProductsMenuClose}
                      sx={{
                        display: 'block',
                        p: 2.5,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.9) 0%, rgba(10, 17, 40, 0.95) 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        height: '100%',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #ff4d4d, #8b0000)',
                          transform: 'scaleX(0)',
                          transition: 'transform 0.3s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-6px) scale(1.02)',
                          boxShadow: '0 15px 35px rgba(255, 77, 77, 0.2)',
                          border: '1px solid rgba(255, 77, 77, 0.4)',
                          '&::before': {
                            transform: 'scaleX(1)',
                          },
                          '& .product-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                          }
                        }
                      }}
                    >
                      {/* Featured Badge */}
                      {product.featured && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            background: 'linear-gradient(45deg, #ff4d4d, #8b0000)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            zIndex: 2,
                          }}
                        >
                          Featured
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          className="product-icon"
                          sx={{
                            fontSize: 32,
                            mr: 2,
                            color: '#ff4d4d',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0,
                          }}
                        >
                          {product.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <MuiTypography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              mb: 0.5,
                              fontSize: '1rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {product.name}
                          </MuiTypography>
                          <MuiTypography
                            variant="caption"
                            sx={{
                              opacity: 0.9,
                              lineHeight: 1.3,
                              fontSize: '0.75rem',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {product.description}
                          </MuiTypography>
                        </Box>
                      </Box>

                      {/* Tags */}
                      {product.tags && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                          {product.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: '0.55rem',
                                borderColor: 'rgba(255, 77, 77, 0.3)',
                                color: 'rgba(255, 255, 255, 0.7)',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>

            {filteredProducts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AutoAwesomeIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                <MuiTypography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  No products found in this category
                </MuiTypography>
                <MuiTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mt: 1 }}>
                  Try selecting a different category
                </MuiTypography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  ), [isMobile, selectedCategory, filteredProducts, handleProductsMenuClose]);

  // Company Dropdown Component
  const CompanyDropdown = useCallback(() => (
    <Paper
      sx={{
        width: isMobile ? '90vw' : 320,
        background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
        border: '1px solid rgba(139, 0, 0, 0.3)',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(20px)',
        animation: `${slideInFromTop} 0.3s ease-out`,
      }}
    >
      <Box sx={{ p: 2 }}>
        <MuiTypography
          variant="h6"
          sx={{
            color: 'white',
            mb: 2,
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff4d4d, #8b0000)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <GroupsIcon sx={{ fontSize: 20 }} />
          Company
        </MuiTypography>

        {companyMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleCompanyMenuClose}
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 1,
              background: location.pathname === item.path
                ? 'linear-gradient(90deg, rgba(139, 0, 0, 0.3) 0%, transparent 100%)'
                : 'transparent',
              border: location.pathname === item.path
                ? '1px solid rgba(255, 77, 77, 0.5)'
                : '1px solid transparent',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'linear-gradient(90deg, rgba(139, 0, 0, 0.2) 0%, transparent 100%)',
                border: '1px solid rgba(255, 77, 77, 0.3)',
                transform: 'translateX(8px)',
                '& .menu-item-badge': {
                  transform: 'scale(1.1)',
                }
              }
            }}
          >
            <Box sx={{
              color: location.pathname === item.path ? '#ff4d4d' : 'rgba(255,255,255,0.7)',
              mr: 2,
              display: 'flex',
              alignItems: 'center'
            }}>
              {item.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MuiTypography
                  variant="body1"
                  sx={{
                    color: location.pathname === item.path ? '#ff4d4d' : 'white',
                    fontWeight: location.pathname === item.path ? '600' : '400',
                  }}
                >
                  {item.text}
                </MuiTypography>
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    className="menu-item-badge"
                    sx={{
                      height: 16,
                      fontSize: '0.55rem',
                      bgcolor: item.badge === 'Hiring' ? '#4caf50' : '#ff4d4d',
                      color: 'white',
                      fontWeight: 'bold',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                )}
              </Box>
              <MuiTypography
                variant="caption"
                sx={{
                  color: location.pathname === item.path ? '#ff9999' : 'rgba(255,255,255,0.5)',
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {item.description}
              </MuiTypography>
            </Box>
          </MenuItem>
        ))}
      </Box>
    </Paper>
  ), [isMobile, location.pathname, companyMenuItems, handleCompanyMenuClose]);
  // Mobile Drawer Content
  const drawer = (
    <Box sx={{
      background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Drawer Header */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid rgba(139, 0, 0, 0.3)',
        background: 'linear-gradient(90deg, #0a1128 0%, #1a1f3d 100%)',
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={logo}
                alt="Zolang Nexora Logo"
                sx={{
                  height: 70,
                  mr: 2,
                  objectFit: "contain",
                }}
              />
            </Box>

          </Box>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#ff4d4d' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Call Button in Drawer */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<CallIcon />}
          onClick={handleCallClick}
          sx={{
            background: 'linear-gradient(45deg, #8b0000, #b30000)',
            animation: `${blinkAnimation} 2s infinite ease-in-out`,
            fontWeight: 'bold',
            py: 1.5,
            borderRadius: 2,
            fontSize: '1rem',
            '&:hover': {
              background: 'linear-gradient(45deg, #b30000, #8b0000)',
              animation: 'none',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Call +91&nbsp;6382585971
        </Button>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, p: 0, overflow: 'auto' }}>
        {menuItems.map((item) => (
          <Box key={item.text}>
            {item.hasDropdown ? (
              <>
                <ListItem
                  button
                  onClick={item.isCompany ? handleMobileCompanyToggle : handleMobileProductsToggle}
                  sx={{
                    background: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                      (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                      ? 'linear-gradient(90deg, rgba(139, 0, 0, 0.2) 0%, transparent 100%)'
                      : 'transparent',
                    borderLeft: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                      (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                      ? '4px solid #ff4d4d'
                      : '4px solid transparent',
                    transition: 'all 0.3s ease',
                    py: 2,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{item.text}</span>
                          {item.badge && (
                            <Chip
                              label={item.badge}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                bgcolor: '#ff4d4d',
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                        {(item.isCompany ? mobileCompanyOpen : mobileProductsOpen) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </Box>
                    }
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                          (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                          ? '#ff4d4d'
                          : 'white',
                        fontWeight: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                          (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                          ? 'bold'
                          : 'normal',
                        fontSize: '1rem',
                      }
                    }}
                  />
                </ListItem>
                <Collapse in={item.isCompany ? mobileCompanyOpen : mobileProductsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(item.isCompany ? companyMenuItems : productsData).map((dropdownItem) => (
                      <ListItem
                        key={dropdownItem.text || dropdownItem.id}
                        button
                        component={Link}
                        to={dropdownItem.path}
                        onClick={handleDrawerToggle}
                        sx={{
                          pl: 4,
                          py: 1.5,
                          background: location.pathname === dropdownItem.path
                            ? 'linear-gradient(90deg, rgba(139, 0, 0, 0.15) 0%, transparent 100%)'
                            : 'transparent',
                          borderLeft: location.pathname === dropdownItem.path ? '4px solid #ff4d4d' : '4px solid transparent',
                          '&:hover': {
                            background: 'linear-gradient(90deg, rgba(139, 0, 0, 0.1) 0%, transparent 100%)',
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {dropdownItem.icon && (
                                <Box sx={{ mr: 1, fontSize: 18, color: '#ff4d4d' }}>{dropdownItem.icon}</Box>
                              )}
                              {dropdownItem.name || dropdownItem.text}
                              {dropdownItem.featured && (
                                <Chip
                                  label="Featured"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    height: 16,
                                    fontSize: '0.6rem',
                                    bgcolor: '#ff4d4d',
                                    color: 'white',
                                  }}
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: location.pathname === dropdownItem.path ? '#ff4d4d' : 'rgba(255,255,255,0.8)',
                              fontSize: '0.9rem',
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem
                button
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  py: 2,
                  background: location.pathname === item.path
                    ? 'linear-gradient(90deg, rgba(139, 0, 0, 0.2) 0%, transparent 100%)'
                    : 'transparent',
                  borderLeft: location.pathname === item.path ? '4px solid #ff4d4d' : '4px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(90deg, rgba(139, 0, 0, 0.1) 0%, transparent 100%)',
                    borderLeft: '4px solid #8b0000',
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.text}
                      {item.badge && (
                        <Chip
                          label={item.badge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            bgcolor: '#ff4d4d',
                            color: 'white',
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: location.pathname === item.path ? '#ff4d4d' : 'white',
                      fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                      fontSize: '1rem',
                    }
                  }}
                />
              </ListItem>
            )}
          </Box>
        ))}
      </List>

      {/* Drawer Footer */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid rgba(139, 0, 0, 0.3)',
        textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
          Zolang Nexora Softwares
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Software Development & IT Consultancy
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: scrolled
            ? 'rgba(10, 17, 40, 0.98)'
            : 'linear-gradient(90deg, #0a1128 0%, #1a1f3d 100%)',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          boxShadow: scrolled
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: scrolled ? '1px solid rgba(139, 0, 0, 0.2)' : 'none',
          zIndex: 1200,
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ py: 1, px: { xs: 1, sm: 2, md: 3 } }}>
            {/* Logo Section */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                animation: `${slideInFromTop} 0.6s ease-out`,
                textDecoration: 'none',
                mr: 3,
              }}
            >
              <Box
                  component="img"
                  src={logo}
                  alt="Zolang Nexora Logo"
                  sx={{
                    height: 75,
                    objectFit: "contain",
                  }}
                />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    animation: `${slideInFromTop} 0.6s ease-out 0.2s both`,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                {/* Navigation Links */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {menuItems.map((item, index) => (
                    item.hasDropdown ? (
                      <Box key={item.text}>
                        <Button
                          color="inherit"
                          onClick={item.isCompany ? handleCompanyMenuOpen : handleProductsMenuOpen}
                          endIcon={<KeyboardArrowDownIcon />}
                          startIcon={item.isCompany ? <GroupsIcon sx={{ fontSize: 18 }} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                          sx={{
                            position: 'relative',
                            fontWeight: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                              (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                              ? '700'
                              : '400',
                            color: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                              (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                              ? '#ff4d4d'
                              : 'rgba(255,255,255,0.9)',
                            fontSize: '0.95rem',
                            textTransform: 'none',
                            animation: `${slideInFromTop} 0.6s ease-out ${index * 0.1 + 0.4}s both`,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: '50%',
                              width: (item.isCompany && companyMenuItems.some(menuItem => location.pathname === menuItem.path)) ||
                                (!item.isCompany && (location.pathname.startsWith('/products') || productsData.some(p => p.path === location.pathname)))
                                ? '100%'
                                : '0%',
                              height: '3px',
                              background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'translateX(-50%)',
                              borderRadius: '2px',
                            },
                            '&:hover': {
                              color: '#ff4d4d',
                              transform: 'translateY(-2px)',
                              background: 'rgba(255, 77, 77, 0.1)',
                              '&::before': {
                                width: '100%',
                              },
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.text}
                            {item.badge && (
                              <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: '#ff4d4d',
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            )}
                          </Box>
                        </Button>

                        {/* Dropdown Menus */}
                        <Menu
                          anchorEl={item.isCompany ? companyAnchor : productsAnchor}
                          open={Boolean(item.isCompany ? companyAnchor : productsAnchor)}
                          onClose={item.isCompany ? handleCompanyMenuClose : handleProductsMenuClose}
                          TransitionComponent={Fade}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              overflow: 'visible',
                              filter: 'drop-shadow(0px 10px 30px rgba(0,0,0,0.5))',
                              mt: 1.5,
                              background: 'transparent',
                            },
                          }}
                          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                          {item.isCompany ? <CompanyDropdown /> : <ProductsDropdown />}
                        </Menu>
                      </Box>
                    ) : (
                      <Button
                        key={item.text}
                        color="inherit"
                        component={Link}
                        to={item.path}
                        sx={{
                          position: 'relative',
                          fontWeight: location.pathname === item.path ? '700' : '400',
                          color: location.pathname === item.path ? '#ff4d4d' : 'rgba(255,255,255,0.9)',
                          fontSize: '0.95rem',
                          textTransform: 'none',
                          animation: `${slideInFromTop} 0.6s ease-out ${index * 0.1 + 0.4}s both`,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            width: location.pathname === item.path ? '100%' : '0%',
                            height: '3px',
                            background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: 'translateX(-50%)',
                            borderRadius: '2px',
                          },
                          '&:hover': {
                            color: '#ff4d4d',
                            transform: 'translateY(-2px)',
                            background: 'rgba(255, 77, 77, 0.1)',
                            '&::before': {
                              width: '100%',
                            },
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.text}
                          {item.badge && (
                            <Chip
                              label={item.badge}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                bgcolor: '#ff4d4d',
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                        </Box>
                      </Button>
                    )
                  ))}
                </Box>

                {/* Divider */}
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 1 }} />

                {/* Blinking Call Button */}
                <Box
                  sx={{
                    position: 'relative',
                    animation: `${slideInFromTop} 0.6s ease-out 0.7s both`,
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    overlap="circular"
                    sx={{
                      '& .MuiBadge-dot': {
                        animation: `${blinkAnimation} 1.5s infinite ease-in-out`,
                        backgroundColor: '#ff4d4d',
                        boxShadow: '0 0 10px #ff4d4d',
                      }
                    }}
                  >
                    <Tooltip title="Call us now!" arrow placement="bottom">
                      <Button
                        variant="contained"
                        startIcon={isHovered ? <CallIcon /> : <PhoneIcon />}
                        onClick={handleCallClick}
                        sx={{
                          background: isHovered
                            ? 'linear-gradient(45deg, #b30000, #8b0000)'
                            : 'linear-gradient(45deg, #8b0000, #b30000)',
                          animation: isHovered
                            ? 'none'
                            : `${blinkAnimation} 2s infinite ease-in-out, ${pulseGlow} 3s infinite ease-in-out`,
                          fontWeight: 'bold',
                          px: 3,
                          py: 1.2,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          minWidth: 'auto',
                          boxShadow: isHovered
                            ? '0 8px 25px rgba(139, 0, 0, 0.4)'
                            : '0 4px 15px rgba(139, 0, 0, 0.3)',
                          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: '0 12px 30px rgba(139, 0, 0, 0.5)',
                          }
                        }}
                      >
                        {isHovered ? 'Call Now' : '+91 6382585971'}
                      </Button>
                    </Tooltip>
                  </Badge>
                </Box>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 320,
            borderRight: '1px solid rgba(139, 0, 0, 0.3)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer for fixed header */}
      <Toolbar />
    </>
  );
};

export default React.memo(Header);